<template>
  <Galleria
    :key="files.length"
    v-model:visible="visible"
    v-model:active-index="activeIndex"
    class="FMFileGalleria"
    :value="files"
    :num-visible="numVisible"
    :circular="true"
    :full-screen="true"
    :show-item-navigators="files.length > 1"
    :show-thumbnail-navigators="numVisible < files.length"
    :auto-play="autoplayIntervalInMs != null"
    :dismissable-mask="true"
    :transition-interval="autoplayIntervalInMs"
    :pt="{
      mask: {
        onMousedown: onMaskMouseDown,
        onMouseup: onMaskMouseUp,
        onMouseleave: onMaskPointerCancel,
        onTouchstart: onMaskTouchStart,
        onTouchend: onMaskTouchEnd,
        onTouchcancel: onMaskPointerCancel,
      },
      thumbnailContent: {
        style: 'border-top: 1px solid var(--p-gray-300); ',
      },
      nextButton: {
        class: 'FMFileGalleriaButton',
      },
      prevButton: {
        class: 'FMFileGalleriaButton',
      },
      closeButton: {
        class: 'FMFileGalleriaButton',
      },
    }"
  >
    <template #item="slotProps">
      <div class="bg-white p-3" style="height: 80vh; width: 90vw">
        <ZoomLayer v-if="isZoomAllowed(slotProps.item)" :reload-key="activeIndex">
          <FMFileViewer v-if="slotProps.item != null" :file="slotProps.item" :big-shadow="true" />
        </ZoomLayer>
        <FMFileViewer v-else-if="slotProps.item != null" :file="slotProps.item" />
      </div>
    </template>
    <template #thumbnail="slotProps">
      <FMFilePreview
        :file="slotProps.item"
        :hide-without-thumbnail="false"
        :hover-and-shadow-effect-visible="true"
        class="mb-2"
        size-x="4em"
        size-y="4em"
      />
    </template>
  </Galleria>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch, type PropType } from "vue";
import FMFileViewer from "./FMFileViewer.vue";
import Galleria from "primevue/galleria";
import FMFilePreview from "./FMFilePreview.vue";
import type { FMFileForGallery } from "./FMFileUploadHelper";
import ZoomLayer from "./ZoomLayer.vue";
import { isZoomAllowed } from "../helper/FMFilePreviewHelper";

const visible = defineModel("visible", {
  type: Boolean,
  required: true,
});

const activeIndex = defineModel("activeIndex", {
  type: Number,
  required: false,
  default: 0,
});

const props = defineProps({
  files: {
    type: Array as PropType<FMFileForGallery[]>,
    required: true,
  },
  autoplayIntervalInMs: {
    type: Number,
    required: false,
    default: undefined,
  },
});

watch(
  () => ({
    activeIndex: activeIndex.value,
    fileLength: props.files.length,
  }),
  () => {
    if (activeIndex.value >= props.files.length || activeIndex.value < 0) {
      activeIndex.value = 0;
    }
  },
  {
    immediate: true,
  },
);

const viewportWidth = ref(window.innerWidth);
const rootFontSize = ref(16);

function updateSizes() {
  viewportWidth.value = window.innerWidth;
  rootFontSize.value = parseFloat(getComputedStyle(document.documentElement).fontSize);
}

onMounted(() => {
  updateSizes();
  window.addEventListener("resize", updateSizes);
});

onUnmounted(() => {
  window.removeEventListener("resize", updateSizes);
});

type PressStart = {
  x: number;
  y: number;
};

const maskPressStarted = ref(false);
const maskPressStartPoint = ref<PressStart | null>(null);
const maxTapMove = 8;

function isDirectMaskHit(event: Event) {
  return event.target === event.currentTarget;
}

function startMaskPress(x: number, y: number) {
  maskPressStarted.value = true;
  maskPressStartPoint.value = { x, y };
}

function resetMaskPress() {
  maskPressStarted.value = false;
  maskPressStartPoint.value = null;
}

function shouldCloseFromEnd(x: number, y: number) {
  if (!maskPressStarted.value || !maskPressStartPoint.value) {
    return false;
  }

  const dx = x - maskPressStartPoint.value.x;
  const dy = y - maskPressStartPoint.value.y;
  return Math.hypot(dx, dy) <= maxTapMove;
}

function onMaskMouseDown(event: MouseEvent) {
  if (!isDirectMaskHit(event)) {
    resetMaskPress();
    return;
  }

  startMaskPress(event.clientX, event.clientY);
}

function onMaskMouseUp(event: MouseEvent) {
  const shouldClose = isDirectMaskHit(event) && shouldCloseFromEnd(event.clientX, event.clientY);

  resetMaskPress();

  if (shouldClose) {
    visible.value = false;
  }
}

function onMaskTouchStart(event: TouchEvent) {
  if (!isDirectMaskHit(event) || event.touches.length !== 1) {
    resetMaskPress();
    return;
  }

  const touch = event.touches[0];
  startMaskPress(touch.clientX, touch.clientY);
}

function onMaskTouchEnd(event: TouchEvent) {
  if (!maskPressStarted.value || !isDirectMaskHit(event)) {
    resetMaskPress();
    return;
  }

  const touch = event.changedTouches[0];
  if (!touch) {
    resetMaskPress();
    return;
  }

  const shouldClose = isDirectMaskHit(event) && shouldCloseFromEnd(touch.clientX, touch.clientY);

  resetMaskPress();

  if (shouldClose) {
    event.preventDefault(); // Prevents mouse events from also firing for mask taps.
    visible.value = false;
  }
}

function onMaskPointerCancel() {
  resetMaskPress();
}

const numVisible = computed(() => {
  const availableWidth = viewportWidth.value * 0.9; // 90vw
  const thumbnailWidth = rootFontSize.value * 5; // 5em
  return Math.max(1, Math.min(props.files.length, Math.floor(availableWidth / thumbnailWidth)));
});

function onKeyUp(event: KeyboardEvent) {
  if (!visible.value) {
    return;
  }

  if (event.key === "Escape") {
    visible.value = false;
  } else if (event.key === "ArrowRight") {
    activeIndex.value = (activeIndex.value + 1) % props.files.length;
  } else if (event.key === "ArrowLeft") {
    activeIndex.value = (activeIndex.value - 1 + props.files.length) % props.files.length;
  }
}

onMounted(() => {
  document.addEventListener("keyup", onKeyUp, { passive: true });
});

onUnmounted(() => {
  document.removeEventListener("keyup", onKeyUp);
});
</script>
<style lang="scss">
.FMFileGalleriaButton {
  background: var(--p-gray-200) !important;
  color: var(--p-gray-700) !important;
  z-index: 9000;
  transition:
    transform 0.2s,
    background 0.2s,
    color 0.2s;

  &:hover {
    transform: scale(1.1);
    background: var(--p-gray-300) !important;
    color: var(--p-gray-900) !important;
  }
}
</style>
